// src/hooks/useLiveGames.js
//
// Hook para escuchar /live_games en RTDB en tiempo real.
//
// El bot Edge Elite (FASE V7) sube cada partido analizado a:
//   /live_games/{game_id}
// con schema:
//   {
//     game_id, timestamp, partido, away_team, home_team, liga,
//     marcador: { home, away, total },
//     periodo, reloj, clock_raw, linea, edge, edge_ratio, projection, ventaja,
//     v6: { categoria, razon, razon_humana, passed, would_emit, ... },
//     categoria_dashboard, telegram_emit
//   }
//
// Devuelve:
//   { games, loading, error, byId }
//   - games: array de partidos (cada uno = objeto schema arriba)
//   - byId: Map<game_id, game> para lookup rápido al cruzar con /picks
//   - loading: true hasta el primer snapshot
//   - error: instancia Error si onValue falló
//
// El listener se desconecta automáticamente al desmontar el componente.

import { useEffect, useState } from 'react';
import { onValue, ref } from 'firebase/database';
import { db } from '../firebase';
import { isHiddenLeague } from '../config/hiddenLeagues';

export function useLiveGames() {
  const [games, setGames] = useState([]);
  const [byId, setById] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const liveRef = ref(db, 'live_games');
    const unsub = onValue(
      liveRef,
      (snap) => {
        const data = snap.val();
        if (!data || typeof data !== 'object') {
          setGames([]);
          setById(new Map());
          setLoading(false);
          return;
        }
        // Ligas ocultas del dashboard (ej: MPBL sin línea BetsAPI) — se
        // excluyen acá para que TODOS los consumers del hook (lista,
        // cards, counts) queden consistentes. Ver config/hiddenLeagues.js.
        const list = Object.values(data).filter((g) => !isHiddenLeague(g && g.liga));
        // Map para joinear por game_id desde otros consumers (LivePage hace
        // merge de /live_games con /picks por id).
        const map = new Map();
        list.forEach((g) => {
          if (g && g.game_id) map.set(String(g.game_id), g);
        });
        setGames(list);
        setById(map);
        setLoading(false);
      },
      (err) => {
        console.error('[useLiveGames] onValue error:', err);
        setError(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  return { games, byId, loading, error };
}

export default useLiveGames;
